import { Info } from '@phosphor-icons/react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';
import type { AnswerOptionCondition, ExpirationComparison } from 'shared/types/due-diligence';

interface LinkedLabel {
  id: number;
  name: string;
}

interface ExpirationDateEditorProps {
  condition: AnswerOptionCondition;
  onUpdate: (updates: Partial<AnswerOptionCondition>) => void;
  linkedLabels?: LinkedLabel[];
}

export function ExpirationDateEditor({ condition, onUpdate, linkedLabels }: ExpirationDateEditorProps) {
  const comparisonOptions = [
    { value: 'more_than_days', label: 'Geldig voor meer dan X dagen' },
    { value: 'within_days', label: 'Verloopt binnen X dagen' },
    { value: 'expired', label: 'Verlopen' },
    { value: 'not_expired', label: 'Niet verlopen' },
  ];

  const currentComparison = condition.expirationComparison || 'more_than_days';
  const showDaysInput = currentComparison === 'more_than_days' || currentComparison === 'within_days';

  const selectedLabel = linkedLabels?.find((l) => l.id === condition.labelId);
  const labelNames = linkedLabels?.map((l) => l.name) || [];

  const getExplanationText = () => {
    switch (currentComparison) {
      case 'more_than_days':
        return `Deze conditie controleert of de vervaldatum verder dan ${condition.expirationDays || 0} dagen in de toekomst ligt.`;
      case 'within_days':
        return `Deze conditie controleert of de vervaldatum binnen ${condition.expirationDays || 0} dagen verloopt.`;
      case 'expired':
        return 'Deze conditie controleert of de vervaldatum in het verleden ligt (het document is verlopen).';
      case 'not_expired':
        return 'Deze conditie controleert of de vervaldatum in de toekomst ligt (het document is nog geldig).';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-4">
      {/* Label context info */}
      {linkedLabels && linkedLabels.length > 0 && (
        <div className="flex items-start gap-2 text-sm bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
          <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
          <div>
            {condition.labelId && selectedLabel ? (
              <p>
                Wordt gecontroleerd voor label:{' '}
                <span className="font-semibold">{selectedLabel.name}</span>
              </p>
            ) : (
              <p>
                Wordt gecontroleerd voor alle gekoppelde labels:{' '}
                <span className="font-semibold">{labelNames.join(', ')}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Data source explanation */}
      <div className="text-sm bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
        <p className="font-medium mb-1.5 text-amber-800 dark:text-amber-300">Databron</p>
        <p className="text-amber-700 dark:text-amber-400 mb-2">
          De vervaldatum wordt gelezen uit het veld <span className="font-semibold">Vervaldatum (expirationDate)</span> op het bestandslogboek (FileLog).
        </p>
        <p className="text-amber-700 dark:text-amber-400 mb-1">Dit veld kan op twee manieren worden ingevuld:</p>
        <ol className="list-decimal list-inside text-amber-700 dark:text-amber-400 space-y-0.5 ml-1">
          <li><span className="font-semibold">Handmatig</span> — u vult de datum zelf in bij het bestandsoverzicht</li>
          <li><span className="font-semibold">AI extractie</span> — de AI leest de vervaldatum automatisch uit het document in de Dropzone</li>
        </ol>
        <p className="text-amber-700 dark:text-amber-400 mt-2 text-xs italic">
          Wanneer deze datum wijzigt, wordt de conditie automatisch opnieuw geëvalueerd.
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Vervaldag vergelijking</Label>
        <SimpleDropdown
          value={currentComparison}
          onChange={(value) => onUpdate({ expirationComparison: value as ExpirationComparison })}
          options={comparisonOptions}
          placeholder="Selecteer vergelijking..."
        />
      </div>
      {showDaysInput && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Aantal dagen</Label>
          <Input
            type="number"
            min="0"
            value={condition.expirationDays || 0}
            onChange={(e) => onUpdate({ expirationDays: parseInt(e.target.value, 10) || 0 })}
            className="text-sm"
          />
        </div>
      )}
      <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg border border-border">
        <p className="font-medium mb-1">Uitleg:</p>
        <p>{getExplanationText()}</p>
      </div>
    </div>
  );
}
